import { useDebouncedSearch, useLoading } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useDebounceFunction } from '../../../hooks/useDebounce';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { getFleetMaintenanceRules } from '../../../services/fleetService';
import { apiParamsState } from '../../../storage/app';
import { FleetMaintenanceRule } from '../../../types/fleet';
import { EnhancedLabeledSelect } from '../../ui/EnhancedLabeledSelect';

type Props = {
  maintenanceRule?: FleetMaintenanceRule;
  selectedMaintenanceRuleId?: string;
  onChangeMaintenanceRuleId?: (maintenanceRuleId?: string) => void;
  onChangeMaintenanceRule?: (maintenanceRule?: FleetMaintenanceRule) => void;
  label?: string;
  maxDropdownHeight?: string;
  emptyOptionText?: string;
  showEmptyOption?: boolean;
  helpText?: React.ReactNode;
  error?: React.ReactNode;
  defaultSearch?: string;
  required?: boolean;
  isFilter?: boolean;
  inline?: boolean;
  placeholder?: string;
};

function FleetMaintenanceRuleSelect({
  selectedMaintenanceRuleId,
  onChangeMaintenanceRuleId,
  onChangeMaintenanceRule,
  maintenanceRule,
  label = 'Regla:',
  showEmptyOption = false,
  emptyOptionText = 'Selecciona una regla',
  placeholder,
  maxDropdownHeight,
  helpText,
  error,
  defaultSearch,
  isFilter = false,
  required = false,
  inline,
}: Readonly<Props>) {
  const apiParams = useRecoilValue(apiParamsState);
  const [maintenanceRuleList, setMaintenanceRuleList] = useState<FleetMaintenanceRule[]>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const { search, setSearch, trigger: debouncedSearch } = useDebouncedSearch(defaultSearch ?? '');
  const [selectedMaintenanceRuleOption, setSelectedMaintenanceRuleOption] = useState<{
    id: string;
    label: string;
  }>();
  const requestIdRef = useRef(0);

  useUpdateEffect(() => {
    setPage(0);
  }, [debouncedSearch, apiParams?.clientId, apiParams?.subClientId]);

  useEffect(() => {
    if (!apiParams) return;
    if (page === 0) {
      setPage(1);
      return;
    }
    startLoading();
    const loadMaintenanceRules = async () => {
      const requestId = ++requestIdRef.current;
      const { configurations, error, hasNext } = await getFleetMaintenanceRules({
        page,
        clientId: apiParams.clientId,
        subClientId: apiParams.subClientId,
        search,
      });
      if (requestId === requestIdRef.current) {
        if (!error && configurations && hasNext !== undefined) {
          if (page > 1 && maintenanceRuleList) {
            setMaintenanceRuleList([...maintenanceRuleList, ...configurations]);
            setHasNextPage(hasNext);
          } else {
            setMaintenanceRuleList(configurations);
            setHasNextPage(hasNext);
          }
        } else {
          setMaintenanceRuleList(undefined);
          setHasNextPage(false);
        }
        stopLoading();
      }
    };
    loadMaintenanceRules();
  }, [page]);

  const maintenanceRuleOptions = useMemo(() => {
    if (!maintenanceRuleList) return;
    const maintenanceRulesOptions: { id: string | undefined; label: string }[] = maintenanceRuleList.map(
      (maintenanceRule) => ({
        id: maintenanceRule._id,
        label: maintenanceRule.name,
      }),
    );
    if (showEmptyOption) {
      maintenanceRulesOptions?.unshift({
        id: undefined,
        label: isFilter ? 'Todas' : emptyOptionText,
      });
    }
    if (maintenanceRule) {
      if (!maintenanceRulesOptions.find((c) => c.id === maintenanceRule._id))
        maintenanceRulesOptions.push({
          id: maintenanceRule._id,
          label: maintenanceRule.name,
        });
    }
    if (selectedMaintenanceRuleOption) {
      if (!maintenanceRulesOptions.find((c) => c.id === selectedMaintenanceRuleOption.id))
        maintenanceRulesOptions.push(selectedMaintenanceRuleOption);
    }
    return maintenanceRulesOptions;
  }, [emptyOptionText, isFilter, maintenanceRule, maintenanceRuleList, selectedMaintenanceRuleOption, showEmptyOption]);

  const onMaintenanceRuleChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeMaintenanceRuleId?.(params.option.id as string);
      if (onChangeMaintenanceRule) {
        const asset = maintenanceRuleList?.find((asset) => asset._id === (params.option?.id as string));
        onChangeMaintenanceRule(asset);
      }
    } else {
      onChangeMaintenanceRuleId?.(undefined);
      onChangeMaintenanceRule?.(undefined);
    }
    setSelectedMaintenanceRuleOption(params.option as { id: string; label: string });
    setSearch(undefined);
  };

  const fetchData = () => {
    if (hasNextPage) setPage(page + 1);
  };

  const handleInputChange = useDebounceFunction(function (term: string) {
    setSearch(term);
  }, 350);

  return (
    <EnhancedLabeledSelect
      label={label}
      options={maintenanceRuleOptions}
      value={
        showEmptyOption || selectedMaintenanceRuleId !== undefined ? [{ id: selectedMaintenanceRuleId }] : undefined
      }
      onChange={onMaintenanceRuleChange}
      onInputChange={(e) => {
        handleInputChange(e.target.value);
      }}
      infiniteScroll
      inline={inline}
      inset={inline}
      placeholder={placeholder}
      fetchData={fetchData}
      fetchDataLoading={loading}
      hasNext={hasNextPage}
      searchable
      maxDropdownHeight={maxDropdownHeight}
      defaultSearch={defaultSearch}
      onBlurResetsInput={false}
      caption={helpText}
      error={error}
      required={required}
    />
  );
}

export default FleetMaintenanceRuleSelect;
